import React, {useMemo} from 'react';
import moment from 'moment';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {t} from '../../../../../../services/i18n';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import HStack from '../../../../../common/Layout/HStack';
import VStack from '../../../../../common/Layout/VStack';
import AppText from '../../../../../common/AppText';
import {dbDateFormat, daysEnum, getSeriesNameForSort, sortCourseEventDays, sortString} from '../../../../../../services/Helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { GlobalConsts } from '../../../../../../styles/GlobalConsts';

const TrackCourseRow = ({series}) => {
    const box = useSelector((state) => state.box.box);
    const dateFormat = useMemo(() => box?.locations_box?.[0]?.date_format, [box]);

    const formatDateRange = () => {
        if (!series.start_date) return null;
        const start = moment(series.start_date, dbDateFormat).format(dateFormat ?? 'DD/MM/YY');
        if (!series.end_date) return t('start-on', {start});
        const end = moment(series.end_date, dbDateFormat).format(dateFormat ?? 'DD/MM/YY');
        return `${start} - ${end}`;
    };

    const formatDays = () => {
        const seriesEvents = series.series_events ?? [];
        if (!seriesEvents.length) return null;
        const isOneDayOfWeek = seriesEvents.length === 1;
        const days = sortCourseEventDays(seriesEvents).map(day => {
            if (isOneDayOfWeek) return t(`day-${day.day_of_week + 1}`);
            const dayName = daysEnum[day.day_of_week];
            return t(`${dayName}Shortened`);
        });
        return isOneDayOfWeek ? t('in-day', {day: days[0]}) : t('in-days', {days: days.join(', ')});
    };

    const formatSpotsRemaining = () => {
        if (series.isHugFull) return null;
        const remaining = series.remainingSlots ?? (
            series.max_users != null && series.series_membership_user
                ? series.max_users - series.series_membership_user.length
                : null
        );
        return remaining != null ? t('spots-remaining', {count: remaining}) : null;
    };

    const dateRange = formatDateRange();
    const days = formatDays();
    const spots = formatSpotsRemaining();

    return (
        <HStack alignItems={'center'} flex={1} space={'0.5rem'} style={{minWidth: 0}}>
            <AppText fontSize={GlobalStyleAttributes.fonts.standard16}>
                {`${series.series_name} - ${days}`}
            </AppText>
            <FontAwesomeIcon icon={solid('circle-small')} fontSize={'5px'}/>
            <HStack space={'0.5rem'} flex={'unset'} alignItems={'center'}>
                <FontAwesomeIcon icon={solid('user')} fontSize={GlobalConsts.iconSize.XXS}/>
                <AppText>{spots}</AppText>
            </HStack>
        </HStack>
    );
};

const TrackCollapseBody = ({track}) => {
    const sortedRelations = useMemo(() =>
        _.sortBy(
            track.series_track_relations ?? [],
            relation => sortString(getSeriesNameForSort(relation.series?.series_name)),
        ),
    [track]);

    return (
        <VStack space={'1rem'}>
            <AppText fontSize={GlobalStyleAttributes.fonts.heading3} boldFont>
                {t('courses-included-in-track')}
            </AppText>
            <VStack space={'0.75rem'}>
                {sortedRelations.map(relation => {
                    const series = relation.series;
                    if (!series) return null;
                    return (
                        <TrackCourseRow
                            key={relation.series_id ?? series.id}
                            series={series}
                        />
                    );
                })}
                {!sortedRelations.length && (
                    <AppText fontSize={GlobalStyleAttributes.fonts.heading3}>
                        {t('no-hugim-found')}
                    </AppText>
                )}
            </VStack>
        </VStack>
    );
};

export default TrackCollapseBody;
